import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAlert } from '../Components/AlertContext';
import Breadcrumbs from '../Components/Breadcrumbs';
import Button from '../Components/Buttons';
import { ConfirmationProvider, useConfirmation } from '../Components/ConfirmationContext';
import { CustomInput, CustomTextarea, CustomToggle } from '../Components/CustomFormElements';
import ItemsPickerModal from '../Components/ItemsPickerModal';
import { PickerColumn, PickerItem } from '../Components/ItemsPickerModalTypes';
import PageLoader from '../Components/PageLoader';
import Table from '../Components/Tables';
import { AgentApiProvider, useAgentApi } from '../Services/AgentApiProvider';
import { AssistantApi } from '../Services/AgentApiProviderTypes';
import {
  SimulationDatasetApiProvider,
  useSimulationDatasetApi,
} from '../Services/SimulationDatasetApiProvider';
import {
  EvalConversationShort,
  EvalRunSummary,
} from '../Services/SimulationDatasetApiProviderTypes';
import { formatDateUTC } from '../utils/formatDate';
import './SimulationDatasetConversation.css';

interface AgentPickerItem extends PickerItem {
  id: string;
  key: string;
  name: string;
}

const agentPickerColumns: PickerColumn<AgentPickerItem>[] = [
  {
    key: 'name',
    header: 'Assistant',
    widthClass: 'items-picker-col--flex-3',
    render: (item) => <div className="items-picker-name">{item.name || 'Unnamed assistant'}</div>,
    searchable: true,
    getSearchValue: (item) => item.name || '',
  },
];

function runStatusLabel(run: EvalRunSummary): string {
  if (run.status === 'RUNNING') return `Running… (${run.passed}/${run.total})`;
  return `${run.passed} / ${run.total}`;
}

function SimulationDatasetDetailContent() {
  const { organizationId, datasetKey } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { showAlert } = useAlert();
  const { showConfirmation } = useConfirmation();
  const {
    getDataset,
    createDataset,
    updateDataset,
    deleteConversation,
    startEvaluation,
    getEvaluations,
    getConversations,
  } = useSimulationDatasetApi();
  const { getAgents } = useAgentApi();

  const isNew = !datasetKey || datasetKey === 'new-dataset';

  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [alertShown, setAlertShown] = useState<boolean>(false);
  const [isPickerOpen, setIsPickerOpen] = useState<boolean>(false);

  const [agents, setAgents] = useState<AgentPickerItem[]>([]);

  const [name, setName] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [isActive, setIsActive] = useState<boolean>(true);

  const [conversations, setConversations] = useState<EvalConversationShort[]>([]);
  const [conversationsCount, setConversationsCount] = useState<number>(0);
  const [testsPerPage, setTestsPerPage] = useState<number>(10);
  const [evaluations, setEvaluations] = useState<EvalRunSummary[]>([]);
  const [evaluationsCount, setEvaluationsCount] = useState<number>(0);
  const [evalsPerPage, setEvalsPerPage] = useState<number>(10);

  const fetchTestsTable = async (skip: number, limit: number): Promise<void> => {
    if (isNew || !datasetKey) return;
    if (limit !== testsPerPage) setTestsPerPage(limit);
    const response = await getConversations(datasetKey, skip, limit);
    setConversations(response.items || []);
    setConversationsCount(response.totalCount ?? 0);
  };

  const refreshDataset = async (): Promise<void> => {
    if (isNew || !datasetKey) return;
    const data = await getDataset(datasetKey);
    if (!data) {
      navigate('/404');
      return;
    }
    setName(data.name || '');
    setDescription(data.description || '');
    setIsActive(data.is_active ?? true);
  };

  const fetchEvaluations = async (skip: number, limit: number): Promise<void> => {
    if (isNew || !datasetKey) return;
    if (limit !== evalsPerPage) setEvalsPerPage(limit);
    try {
      const response = await getEvaluations(datasetKey, skip, limit);
      setEvaluations(response.items || []);
      setEvaluationsCount(response.totalCount ?? 0);
    } catch (error) {
      console.error('Error loading evaluations:', error);
    }
  };

  const refreshEvaluations = (): Promise<void> => fetchEvaluations(0, evalsPerPage);

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        setLoading(true);
        const agentList = await getAgents(0, 100);
        setAgents(
          (agentList.items || []).map((a: AssistantApi) => ({
            id: a.key,
            key: a.key,
            name: a.name,
          })),
        );
        await refreshDataset();
        await fetchTestsTable(0, testsPerPage);
        await refreshEvaluations();
      } catch (error) {
        console.error('Error fetching dataset data:', error);
        showAlert('fail', 'Failed to load simulation data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [datasetKey, isNew]);

  useEffect(() => {
    if (location.state?.successMessage && !alertShown) {
      showAlert('success', location.state.successMessage);
      setAlertShown(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.successMessage, alertShown, navigate, location.pathname, showAlert]);

  const onCancel = (): void => {
    navigate(`/organizations/${organizationId}/simulation-datasets`);
  };

  const onSave = async (): Promise<void> => {
    if (!name.trim()) {
      showAlert('fail', 'Name is required!');
      return;
    }

    try {
      setSaving(true);
      if (isNew) {
        const created = await createDataset({
          name: name.trim(),
          description: description.trim() || null,
        });
        navigate(`/organizations/${organizationId}/simulation-datasets/${created.key}`, {
          state: { successMessage: 'Simulation created successfully' },
        });
      } else if (datasetKey) {
        await updateDataset(datasetKey, {
          name: name.trim(),
          description: description.trim() || null,
          is_active: isActive,
        });
        showAlert('success', 'Simulation updated successfully');
      }
      navigate(`/organizations/${organizationId}/simulation-datasets`);
    } catch (error) {
      console.error('Error saving simulation:', error);
      showAlert('fail', 'Failed to save simulation');
    } finally {
      setSaving(false);
    }
  };

  const handleEvaluateSelected = async (picked: AgentPickerItem[]): Promise<void> => {
    setIsPickerOpen(false);
    if (!datasetKey || picked.length === 0) return;
    try {
      await startEvaluation(
        datasetKey,
        picked.map((p) => p.key),
      );
      showAlert('success', 'Evaluation started. It runs in the background.');
      await refreshEvaluations();
    } catch (error) {
      console.error('Error starting evaluation:', error);
      showAlert('fail', 'Failed to start the evaluation');
    }
  };

  const handleRowClicked = (row: EvalConversationShort): void => {
    navigate(
      `/organizations/${organizationId}/simulation-datasets/${datasetKey}/conversations/${row.key}`,
    );
  };

  const handleDeleteConversation = async (selectedIds: string[]): Promise<void> => {
    if (selectedIds.length === 0 || !datasetKey) return;
    const message =
      selectedIds.length === 1
        ? 'Are you sure you want to delete the selected test?'
        : `Are you sure you want to delete ${selectedIds.length} selected tests?`;
    showConfirmation(message, async () => {
      try {
        await Promise.all(selectedIds.map((id) => deleteConversation(datasetKey, id)));
        await fetchTestsTable(0, testsPerPage);
        showAlert('success', 'Test(s) were successfully deleted!');
      } catch (error) {
        showAlert('fail', 'Failed to delete the test(s)!');
      }
    });
  };

  const openEvaluation = (run: EvalRunSummary): void => {
    navigate(
      `/organizations/${organizationId}/simulation-datasets/${datasetKey}/evaluations/${run.run_key}`,
    );
  };

  const evaluationColumns = [
    {
      name: 'Agent Name',
      selector: (row: EvalRunSummary) => row.assistant_name || '—',
      sortable: false,
    },
    {
      name: 'Evaluation Date',
      selector: (row: EvalRunSummary) => (row.started_at ? formatDateUTC(row.started_at) : '—'),
      sortable: false,
    },
    {
      name: 'Status',
      selector: (row: EvalRunSummary) => runStatusLabel(row),
      sortable: false,
    },
  ];

  const conversationColumns = [
    {
      name: 'Name',
      selector: (row: EvalConversationShort) => row.name ?? '—',
      sortable: false,
    },
    {
      name: 'Messages',
      selector: (row: EvalConversationShort) => `Messages: ${row.messages_count}`,
      sortable: false,
    },
  ];

  if (loading) return <PageLoader />;

  return (
    <div className="container">
      <Breadcrumbs />
      <div className="row d-flex gap">
        <div className="radius-yes p-2 bg-yes col-6">
          <h2 className="h2-title">{isNew ? 'Simulation — Create' : 'Simulation — Update'}</h2>

          <div className="gap d-flex flex-column">
            <div className="int-col-4 p-2 border-yes radius-yes full-width-on-sml-scr">
              <h4 className="h4-title">Settings</h4>
              <div className="input-block d-flex flex-row justify-between">
                <div className="input-description">
                  <div className="input-title">Name</div>
                  <div className="small-font">Enter the simulation name</div>
                </div>
                <div className="align-wrapper d-flex align-center">
                  <CustomInput
                    id="dataset-name"
                    label="Name"
                    type="text"
                    placeholder="Name"
                    value={name}
                    onChange={(_: string, value: string) => setName(value)}
                  />
                </div>
              </div>

              <div className="input-block d-flex flex-column">
                <div className="input-description">
                  <div className="input-title">Description</div>
                  <div className="small-font">Optional description</div>
                </div>
                <CustomTextarea
                  id="dataset-description"
                  addedText={description}
                  height="120px"
                  onChange={(value: string) => setDescription(value)}
                />
              </div>

              {!isNew && (
                <div className="input-row input-block d-flex flex-row justify-between">
                  <div className="input-description">
                    <div className="input-title">Active</div>
                    <div className="small-font">Enable or disable this simulation</div>
                  </div>
                  <div className="align-wrapper d-flex align-center">
                    <CustomToggle
                      id="dataset-active"
                      label={isActive ? 'Active' : 'Inactive'}
                      checked={isActive}
                      onChange={(_id: string, value: boolean) => setIsActive(value)}
                    />
                  </div>
                </div>
              )}
            </div>

            {!isNew && (
              <>
                <div className="int-col-4 p-2 border-yes radius-yes full-width-on-sml-scr">
                  <Table
                    title="Evaluations"
                    columns={evaluationColumns}
                    data={evaluations}
                    progressPending={false}
                    paginationPerPage={evalsPerPage}
                    paginationTotalRows={evaluationsCount}
                    onChangePage={fetchEvaluations}
                    onRowClicked={openEvaluation}
                    sortIcon={<i className="fa-solid fa-chevron-down"></i>}
                  />
                  <div className="d-flex gap" style={{ marginTop: 8 }}>
                    <Button
                      background="color2"
                      size="large"
                      disabled={conversationsCount === 0}
                      onClick={() => setIsPickerOpen(true)}
                    >
                      Evaluate
                    </Button>
                  </div>
                </div>

                <div className="int-col-4 p-2 border-yes radius-yes full-width-on-sml-scr">
                  <Table
                    title="Tests"
                    columns={conversationColumns}
                    data={conversations}
                    selectableRows
                    selectableRowsHighlight
                    progressPending={false}
                    paginationPerPage={testsPerPage}
                    paginationTotalRows={conversationsCount}
                    onChangePage={fetchTestsTable}
                    onRowClicked={handleRowClicked}
                    sortIcon={<i className="fa-solid fa-chevron-down"></i>}
                    onDelete={handleDeleteConversation}
                    getRowUrl={(row: EvalConversationShort) =>
                      `/organizations/${organizationId}/simulation-datasets/${datasetKey}/conversations/${row.key}`
                    }
                  />
                  <Button
                    variant="secondary"
                    size="large"
                    onClick={() =>
                      navigate(
                        `/organizations/${organizationId}/simulation-datasets/${datasetKey}/conversations/new-conversation`,
                      )
                    }
                  >
                    Add
                  </Button>
                </div>
              </>
            )}
          </div>

          <div className="buttons-container justify-end gap-12">
            <Button background="color2" size="medium" disabled={saving} onClick={onSave}>
              {isNew ? 'Create' : 'Save'}
            </Button>
            <Button variant="danger" size="medium" disabled={saving} onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </div>
      </div>

      <ItemsPickerModal<AgentPickerItem>
        isOpen={isPickerOpen}
        onClose={() => setIsPickerOpen(false)}
        title="Select assistant(s) to evaluate"
        saveLabel="Evaluate"
        multiSelect
        items={agents}
        onSaveSelected={handleEvaluateSelected}
        columns={agentPickerColumns}
        searchPlaceholder="Search by name..."
      />
    </div>
  );
}

function SimulationDatasetDetail() {
  return (
    <ConfirmationProvider>
      <AgentApiProvider>
        <SimulationDatasetApiProvider>
          <SimulationDatasetDetailContent />
        </SimulationDatasetApiProvider>
      </AgentApiProvider>
    </ConfirmationProvider>
  );
}

export default SimulationDatasetDetail;
